import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/sharedStyles.scss";
import { base64FileDownload } from "../../../utils/FileDownloadUtil";
import TermsDialog from "../../../shared/termsDialog/TermsDialog";
import FileEncoder from "../../../utils/FileEncoder";
import styles from "./CustomerContract.module.scss";
import { ValidatorForm } from "react-material-ui-form-validator";
import Button from "@mui/material/Button";
import CustomerContractHeader from "./CustomerContractHeader";
import { withTranslation } from "react-i18next";
import UploadButton from "../../../imgs/upload.svg";
import CustomerContractRow from "./CustomerContractRow";
import { CUSTOMER_CONTRACT } from "../../../common/Constants";
import PendingActionClient from "../../../shared/clients/PendingActionClient";
import CustomerContractClient from "../../../shared/clients/CustomerContractClient";
import HasPermissionTo from "../../../common/HasPermissionTo";

function CustomerContract({
                            filteredDealers,
                            lang,
                            hideOrShow,
                            user,
                            getPendingActions,
                            isMainViewingSDLR,
                            t,
                            ...restProps
                          }) {
  const [contracts, setContracts] = useState([]);
  const [updateContractForAll, setUpdateContractForAll] = useState(false);
  const [updateContractForADealer, setUpdateContractForADealer] = useState(false);
  const [uploadSuccessForAll, setUploadSuccessForAll] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [contractEmails, setContractEmails] = useState({});
  const [changedEmails, setChangedEmails] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const selectedDealerRef = useRef(null);
  const contractEmailsCopyRef = useRef({});

  const dealerIds = useMemo(() =>
      filteredDealers.map((dealer) => dealer.completeDealerId),
    [filteredDealers]
  );

  const langCode = useMemo(() =>
      lang ? lang.substr(0, 2) : "",
    [lang]
  );

  const viewingAll = useMemo(() =>
      filteredDealers.length > 1,
    [filteredDealers.length]
  );

  const handleContractDownload = useCallback((file) => {
    base64FileDownload(file.contract, "application/pdf", file.fileName);
  }, []);

  const handleEmailChange = useCallback((dealerId, event) => {
    const currentContractEmails = JSON.parse(JSON.stringify(contractEmails));
    currentContractEmails[dealerId] = event.target.value;
    updateChangedList(dealerId, event.target.value, currentContractEmails);
  }, [contractEmails]);

  const updateChangedList = useCallback((dealerId, newEmail, currentContractEmails) => {
    const currentChangedEmails = JSON.parse(JSON.stringify(changedEmails));
    if (newEmail !== contractEmailsCopyRef.current[dealerId]) {
      currentChangedEmails[dealerId] = newEmail;
    } else {
      delete currentChangedEmails[dealerId];
    }
    setChangedEmails(currentChangedEmails);
    setContractEmails(currentContractEmails);
  }, [changedEmails]);

  const getCustomerContract = useCallback((dealerWhoSaved, showMessage) => {
    hideOrShow(true);
    CustomerContractClient.getCustomerContracts(dealerIds)
      .then((contracts) => {
        const contractsList = [];
        const contractEmailsData = {};
        filteredDealers.forEach((dealer) => {
          const contract = contracts[dealer.completeDealerId];

          if (contract) {
            contract.name = dealer.name;
            contract.dealerId = dealer.completeDealerId;
            contract.dealerType = dealer.dealerType;

            if (!contract.email) {
              contract.email = dealer.emailAddress;
            }
            if (showMessage && dealerWhoSaved.includes(contract.dealerId)) {
              contract.showMessage = true;
            }
            contractsList.push(contract);
            contractEmailsData[contract.dealerId] = contract.email;
          }
        });
        setContracts(contractsList);
        setContractEmails(contractEmailsData);
        contractEmailsCopyRef.current = JSON.parse(JSON.stringify(contractEmailsData));
      })
      .finally(() => {
        hideOrShow(false);
      });
  }, [hideOrShow, dealerIds, filteredDealers]);

  const updateAlltheFlags = useCallback(() => {
    setUploadSuccessForAll(false);
    setShowErrorMessage(false);
    setSaveError(false);
    setSaveSuccess(false);
  }, []);

  const saveCustomerContract = useCallback((dealerIds, file) => {
    hideOrShow(true);
    updateAlltheFlags();
    const isUpdateContractForAll = updateContractForAll;
    const isUpdateContractForADealer = updateContractForADealer;

    new FileEncoder()
      .encode(file)
      .then((data) => {
        const customerContract = {
          contract: data,
          fileName: file.name,
          fileType: "pdf",
          langCode: langCode,
        };
        CustomerContractClient.uploadCustomerContract(
          dealerIds,
          customerContract
        )
          .then(() => {
            PendingActionClient.removeDealerPendingAction(
              user,
              dealerIds,
              CUSTOMER_CONTRACT,
              1
            ).then(() => {
              getPendingActions();
            });
            getCustomerContract(dealerIds, isUpdateContractForADealer);
            if (isUpdateContractForAll) {
              setUploadSuccessForAll(true);
            }
          })
          .catch(() => {
            setShowErrorMessage(true);
          })
          .finally(() => {
            hideOrShow(false);
          });
      })
      .finally(() => {
        setUpdateContractForAll(false);
        setUpdateContractForADealer(false);
      });
  }, [
    hideOrShow,
    updateAlltheFlags,
    updateContractForAll,
    updateContractForADealer,
    langCode,
    user,
    getPendingActions,
    getCustomerContract
  ]);

  const hideForRetail = useCallback((dealerType) =>
      isMainViewingSDLR(user) ||
      (user.isMainDealer(user.dealerType) && dealerType === "SDLR"),
    [isMainViewingSDLR, user]
  );

  const updateShowDialogForADealerCall = useCallback((dealer) => {
    setUpdateContractForADealer(prev => !prev);
    selectedDealerRef.current = dealer.dealerId;
  }, []);

  const editEmails = useCallback(() => {
    setEditMode(true);
    setSaveSuccess(false);
  }, []);

  const saveChanges = useCallback(() => {
    hideOrShow(true);
    updateAlltheFlags();
    CustomerContractClient.updateContractEmails({
      contractEmails: changedEmails,
      documentLanguage: langCode,
    })
      .then(() => {
        setEditMode(false);
        setChangedEmails({});
        setSaveSuccess(true);
        setSaveError(false);
        contractEmailsCopyRef.current = JSON.parse(JSON.stringify(contractEmails));
      })
      .catch(() => {
        setSaveError(true);
      })
      .finally(() => hideOrShow(false));
  }, [hideOrShow, updateAlltheFlags, changedEmails, langCode, contractEmails]);

  const cancelChanges = useCallback(() => {
    setEditMode(false);
    setContractEmails(contractEmailsCopyRef.current);
    setChangedEmails({});
    setSaveError(false);
  }, []);

  useEffect(() => {
    getCustomerContract();
  }, [getCustomerContract]);

  return (
    <ValidatorForm onSubmit={saveChanges}>
      <TermsDialog
        open={updateContractForAll}
        forAll={true}
        contracts={contracts}
        dialogBody={t("dealerTermsDialog.branchSubHeader")}
        dialogTitle={t("dealerTermsDialog.branchTitle")}
        saveText={t("dealerTermsDialog.apply")}
        confirmAction={saveCustomerContract}
        onClose={() => setUpdateContractForAll(false)}
      />

      <TermsDialog
        open={updateContractForADealer}
        forAll={false}
        dialogBody={t("dealerTermsDialog.allSubHeader")}
        dialogTitle={t("dealerTermsDialog.allTitle")}
        saveText={t("dealerTermsDialog.apply")}
        currentDealerId={selectedDealerRef.current}
        confirmAction={saveCustomerContract}
        onClose={() => setUpdateContractForADealer(false)}
      />

      <div className="pageWrapper">
        <div className="pagePadding">
          {viewingAll ? (
            <>
              <div className={styles.subHeader}>
                {t("CustomerContract.subTitleMain")}
              </div>
              <HasPermissionTo
                perform={["customerContract:write"]}
                permissions={user.permissions.rolePermissions}
                render={() => {
                  return (
                    <div className={styles.buttonDiv}>
                      <Button
                        onClick={() => setUpdateContractForAll(true)}
                      >
                        <img alt="Upload" src={UploadButton} />
                        <div className={styles.buttonText}>
                          {t("CustomerContract.setForAll")}
                        </div>
                      </Button>
                      {uploadSuccessForAll && (
                        <span className={styles.updateCompleteText}>
                          {t("CustomerContract.updateCompleted")}
                        </span>
                      )}
                    </div>
                  );
                }}
              />

              {showErrorMessage && (
                <div className={styles.errorMessage}>
                  {t("CustomerContract.errorMessage")}{" "}
                </div>
              )}
            </>
          ) : (
            <div className={styles.subHeader}>
              {t("CustomerContract.subTitleBranch")}
            </div>
          )}
        </div>

        <CustomerContractHeader
          filteredDealers={filteredDealers}
          lang={lang}
          hideOrShow={hideOrShow}
          user={user}
          getPendingActions={getPendingActions}
          isMainViewingSDLR={isMainViewingSDLR}
          t={t}
          {...restProps}
          editMode={editMode}
          editEmails={editEmails}
        />

        {contracts.map((contract, key) => {
          return (
            <CustomerContractRow
              filteredDealers={filteredDealers}
              lang={lang}
              hideOrShow={hideOrShow}
              user={user}
              getPendingActions={getPendingActions}
              isMainViewingSDLR={isMainViewingSDLR}
              t={t}
              {...restProps}
              contract={contract}
              hideForRetail={hideForRetail}
              editMode={editMode}
              updateShowDialogForADealer={updateShowDialogForADealerCall}
              handleContractDownload={handleContractDownload}
              contractEmails={contractEmails}
              handleEmailChange={handleEmailChange}
              key={key}
            />
          );
        })}

        {saveError && (
          <div className={styles.saveError}>
            {t("CustomerContract.saveError")}
          </div>
        )}
        {saveSuccess && (
          <div className={styles.saveSuccess}>
            {t("CustomerContract.emailUpdated")}
          </div>
        )}
        {editMode ? (
          Object.keys(changedEmails).length > 0 ? (
            <div className={styles.applyButtonDiv}>
              <button type="submit" data-testid="saveBtn">
                {t("CustomerContract.apply")}
              </button>
            </div>
          ) : (
            <div className={styles.closeButtonDiv}>
              <button onClick={cancelChanges}>
                {t("CustomerContract.close")}
              </button>
            </div>
          )
        ) : null}
      </div>
    </ValidatorForm>
  );
}

export default withTranslation("emp")(CustomerContract);
